import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Rating,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CardMedia,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  LinearProgress,
  CircularProgress,
  Alert,
  TextField,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import RepeatIcon from '@mui/icons-material/Repeat';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { getAgentById, hireAgent } from '../../api';
import { useGlobalContext } from '../../GlobalProvider';

const AgentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { appId } = useGlobalContext();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [hiring, setHiring] = useState(false);

  // Fetch agent data
  useEffect(() => {
    const fetchAgent = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAgentById(id);
        setAgent(response.data.data);
      } catch (err) {
        console.error('Error fetching agent:', err);
        setError('Failed to load agent details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAgent();
    }
  }, [id]);

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setImageDialogOpen(true);
  };

  // Handle hire agent
  const handleHireAgent = async () => {
    if (!agent || !appId) return;

    try {
      setHiring(true);
      const hirePayload = {
        agentMarketplaceId: agent.agentId,
        appId: appId
      };
      
      await hireAgent(hirePayload);
      navigate('/agent-workforce'); // Redirect to workforce page
    } catch (err) {
      console.error('Error hiring agent:', err);
      setError('Failed to hire agent. Please try again.');
    } finally {
      setHiring(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading agent details...
        </Typography>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate('/agent-marketplace')} sx={{ mt: 2 }}>
          Back to Marketplace
        </Button>
      </Container>
    );
  }

  // Agent not found
  if (!agent) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" color="error">
          Agent not found
        </Typography>
        <Button onClick={() => navigate('/agent-marketplace')} sx={{ mt: 2 }}>
          Back to Marketplace
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/agent-marketplace')}
        sx={{ mb: 3 }}
      >
        Back to Marketplace
      </Button>

      {/* Agent Header */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar
                src={agent.icon}
                alt={agent.name}
                sx={{ 
                  width: 120, 
                  height: 120, 
                  mx: 'auto', 
                  mb: 2,
                  border: '4px solid white',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                <Rating value={agent.rating || 0} precision={0.1} readOnly />
                <Typography variant="body2" color="text.secondary">
                  ({agent.rating})
                </Typography>
              </Box>
              <Chip 
                label={agent.category} 
                color="primary" 
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              {agent.name}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              {agent.shortDescription}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <Chip 
                icon={<WorkIcon />} 
                label="Full-time" 
                variant="outlined"
              />
              <Chip 
                icon={<LocationOnIcon />} 
                label="Remote" 
                variant="outlined"
              />
              <Chip 
                icon={<BusinessIcon />} 
                label={agent.category} 
                variant="outlined"
              />
              <Chip 
                label={agent.price}
                color="primary"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>

            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {agent.description}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tasks Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          Core Capabilities
        </Typography>
        <Grid container spacing={3}>
          {agent.tasks && agent.tasks.map((task, index) => (
            <Grid item xs={12} md={6} key={task.id || index}>
              <Card sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      {task.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {task.description}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Chip 
                      icon={<TimerIcon />} 
                      label={task.frequency} 
                      size="small" 
                      variant="outlined"
                    />
                    <Chip 
                      icon={<ScheduleIcon />} 
                      label={task.schedule} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>

                  {task.accomplishments && task.accomplishments.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Key Accomplishments:
                      </Typography>
                      <List dense>
                        {task.accomplishments.map((accomplishment, accIndex) => (
                          <ListItem key={accIndex} sx={{ py: 0.5 }}>
                            <ListItemAvatar>
                              <TrendingUpIcon color="success" fontSize="small" />
                            </ListItemAvatar>
                            <ListItemText 
                              primary={accomplishment}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {task.metrics && Object.keys(task.metrics).length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Performance Metrics:
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries(task.metrics).map(([key, value]) => (
                          <Grid item xs={6} key={key}>
                            <Chip 
                              label={`${key}: ${value}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Reviews Section */}
      {agent.reviews && agent.reviews.length > 0 && (
        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
            Client Reviews
          </Typography>
          <Grid container spacing={3}>
            {agent.reviews.map((review, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {review.reviewer.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {review.reviewer}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Rating value={review.rating} readOnly size="small" />
                          <Typography variant="body2" color="text.secondary">
                            {review.date}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      "{review.comment}"
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Screenshots Section */}
      {agent.screenshots && agent.screenshots.length > 0 && (
        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
            Screenshots & Examples
          </Typography>
          <Grid container spacing={2}>
            {agent.screenshots.map((screenshot, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{ 
                    cursor: 'pointer', 
                    borderRadius: 2,
                    '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                  }}
                  onClick={() => handleImageClick(screenshot)}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={screenshot}
                    alt={`Screenshot ${index + 1}`}
                    sx={{ objectFit: 'cover' }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Conversation Examples Section */}
      {agent.conversationExamples && agent.conversationExamples.length > 0 && (
        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
            Conversation Examples
          </Typography>
          {agent.conversationExamples.map((example, index) => (
            <Accordion key={index} sx={{ mb: 2, borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChatIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    {example.scenario}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 2 }}>
                  {example.conversation && example.conversation.map((message, msgIndex) => (
                    <Box key={msgIndex} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                        User:
                      </Typography>
                      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="body2">
                          {message.userQuestion}
                        </Typography>
                      </Paper>
                      
                      <Typography variant="subtitle2" fontWeight="bold" color="secondary" sx={{ mb: 1 }}>
                        {agent.name}:
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                        <Typography variant="body2">
                          {message.agentResponse}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {/* Call to Action */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Ready to Hire {agent.name}?
        </Typography>
        <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
          Start transforming your city operations today
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Button
                      variant="contained"
                      size="large"
                      onClick={handleHireAgent}
                      disabled={hiring}
                      startIcon={hiring ? <CircularProgress size={20} /> : null}
                      sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'grey.100' }
                      }}
                    >
                      {hiring ? 'Hiring...' : `Hire Now - ${agent.price}`}
                    </Button>
          <Button 
            variant="outlined" 
            size="large"
            sx={{ 
              color: 'white', 
              borderColor: 'white',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Schedule Demo
          </Button>
        </Box>
      </Paper>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Screenshot Preview
          <IconButton
            aria-label="close"
            onClick={() => setImageDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <img 
            src={selectedImage} 
            alt="Screenshot" 
            style={{ width: '100%', height: 'auto' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Close</Button>
                          </DialogActions>
                </Dialog>
              </Container>
            );
};

export default AgentDetails; 